// Note: Truck, MaintenanceEntry, Part are kept for future expansion of notification types
import { formatDistance } from '../utils/distance';

interface Notification {
  id: string;
  type: 'maintenance' | 'fuel' | 'part' | 'alert' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  actionRequired: boolean;
  relatedEntity?: {
    type: 'truck' | 'part' | 'maintenance';
    id: string;
    name: string;
  };
}

interface NotificationPreferences {
  push: boolean;
  maintenanceAlerts: boolean;
  fuelAlerts: boolean;
  partAlerts: boolean;
  quietHours: {
    enabled: boolean;
    start: string; 
    end: string;   
  };
}

type NotificationListener = (notifications: Notification[]) => void;

interface WebPushConfig {
  vapidPublicKey: string;
  subscriptionEndpoint: string;
}

interface WebPushStatus {
  supported: boolean;
  configured: boolean;
  subscribed: boolean;
  permission: NotificationPermission | 'unsupported';
  endpoint?: string;
  message: string;
}

class NotificationService {
  private static instance: NotificationService;
  private notifications: Notification[] = [];
  private listeners: NotificationListener[] = [];
  private preferences: NotificationPreferences;
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
  private currentUserId = this.getSavedUserId();
  private readonly webPushConfigKey = 'fleetfix_web_push_config';
  private readonly webPushSubscriptionKey = 'fleetfix_web_push_subscription';

  private constructor() {
    this.preferences = this.loadPreferences();
    this.loadNotifications();
    this.registerServiceWorker();
    this.startMonitoring();
  }

  public setUserContext(userId: string | null): void {
    const nextUserId = userId || 'anonymous';
    if (nextUserId === this.currentUserId) return;

    this.currentUserId = nextUserId;
    this.preferences = this.loadPreferences();
    this.loadNotifications();
    this.notifyListeners();
  }

  private getSavedUserId(): string {
    if (typeof window === 'undefined') return 'anonymous';

    try {
      const savedUser = localStorage.getItem('fleetfix_user');
      return savedUser ? JSON.parse(savedUser).id || 'anonymous' : 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  private getUserScopedStorageKey(baseKey: string): string {
    return `${baseKey}_${this.currentUserId}`;
  }

  private safeJsonParse<T>(value: string | null, fallback: T): T {
    if (!value) return fallback;
    try {
      return JSON.parse(value) as T;
    } catch (error) {
      console.error('Error parsing FleetFix notification storage:', error);
      return fallback;
    }
  }

  private normalizeTimestamp(value: any): Date {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private getCurrentAppState(): { trucks?: any[]; parts?: any[] } {
    if (typeof window === 'undefined') return {};

    try {
      const userState = localStorage.getItem(`fleetfix_app_state_${this.currentUserId}`);
      if (userState) return JSON.parse(userState);
    } catch (error) {
      console.error('Error reading user notification state:', error);
    }

    return {
      trucks: this.safeJsonParse<any[]>(localStorage.getItem(`fleetfix_trucks_${this.currentUserId}`), []),
      parts: this.safeJsonParse<any[]>(localStorage.getItem(`fleetfix_parts_${this.currentUserId}`), [])
    };
  }

  private loadPreferences(): NotificationPreferences {
    const defaults: NotificationPreferences = {
      push: true,
      maintenanceAlerts: true,
      fuelAlerts: true,
      partAlerts: true,
      quietHours: {
        enabled: false,
        start: '22:00',
        end: '07:00'
      }
    };

    const saved = this.safeJsonParse<Partial<NotificationPreferences> | null>(
      localStorage.getItem(this.getUserScopedStorageKey('notificationPreferences')),
      null
    );

    return saved ? { ...defaults, ...saved, quietHours: { ...defaults.quietHours, ...(saved.quietHours || {}) } } : defaults;
  }

  private loadNotifications(): void {
    const parsed = this.safeJsonParse<any[] | null>(
      localStorage.getItem(this.getUserScopedStorageKey('notifications')),
      null
    );

    if (parsed) {
      const parsedNotifications = parsed.map((n: any) => ({
        ...n,
        id: String(n.id || `notification-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
        type: n.type || 'info',
        title: String(n.title || 'FleetFix notification'),
        message: String(n.message || ''),
        timestamp: this.normalizeTimestamp(n.timestamp),
        read: Boolean(n.read),
        priority: n.priority || 'medium',
        actionRequired: Boolean(n.actionRequired)
      }));
      const seen = new Set<string>();
      this.notifications = parsedNotifications
        .sort((a: Notification, b: Notification) => b.timestamp.getTime() - a.timestamp.getTime())
        .filter((notification: Notification) => {
          const key = [
            notification.type,
            notification.title,
            notification.message,
            notification.relatedEntity?.id || ''
          ].join('|');

          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 50);
      this.saveNotifications();
    } else {
      
      this.notifications = [];
      this.saveNotifications();
    }
  }

  private saveNotifications(): void {
    localStorage.setItem(this.getUserScopedStorageKey('notifications'), JSON.stringify(this.notifications));
  }

  private savePreferences(): void {
    localStorage.setItem(this.getUserScopedStorageKey('notificationPreferences'), JSON.stringify(this.preferences));
  }

  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;

    try {
      this.serviceWorkerRegistration = await navigator.serviceWorker.register('/fleetfix-notifications-sw.js');
    } catch (error) {
      console.error('Error registering FleetFix notification worker:', error);
    }
  }

  public getPermissionStatus(): NotificationPermission | 'unsupported' {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  }

  public async requestDesktopPermission(): Promise<NotificationPermission | 'unsupported'> {
    if (!('Notification' in window)) {
      this.updatePreferences({ push: false });
      return 'unsupported';
    }

    if (Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        this.updatePreferences({ push: permission === 'granted' });
        return permission;
      } catch (error) {
        console.error('Error requesting notification permission:', error);
        this.updatePreferences({ push: false });
        return Notification.permission;
      }
    }

    this.updatePreferences({ push: Notification.permission === 'granted' });
    return Notification.permission;
  }

  public canSendDesktopNotifications(): boolean {
    return 'Notification' in window && Notification.permission === 'granted' && this.preferences.push;
  }

  private getWebPushConfig(): WebPushConfig {
    const saved = localStorage.getItem(this.getUserScopedStorageKey(this.webPushConfigKey));
    const savedConfig = saved ? JSON.parse(saved) as Partial<WebPushConfig> : {};

    return {
      vapidPublicKey: savedConfig.vapidPublicKey || process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '',
      subscriptionEndpoint: savedConfig.subscriptionEndpoint || process.env.REACT_APP_PUSH_SUBSCRIPTION_ENDPOINT || ''
    };
  }

  public getWebPushSettings(): WebPushConfig {
    return this.getWebPushConfig();
  }

  public saveWebPushConfig(config: WebPushConfig): WebPushStatus {
    localStorage.setItem(this.getUserScopedStorageKey(this.webPushConfigKey), JSON.stringify(config));
    return this.getWebPushStatus();
  }

  public getWebPushStatus(): WebPushStatus {
    const permission = this.getPermissionStatus();
    const config = this.getWebPushConfig();
    const storedSubscription = localStorage.getItem(this.getUserScopedStorageKey(this.webPushSubscriptionKey));
    const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    const configured = Boolean(config.vapidPublicKey.trim());

    return {
      supported,
      configured,
      subscribed: Boolean(storedSubscription),
      permission,
      endpoint: config.subscriptionEndpoint || undefined,
      message: !supported
        ? 'This browser does not support Web Push.'
        : !configured
          ? 'Add a VAPID public key to activate closed-app push subscriptions.'
          : storedSubscription
            ? 'This browser is subscribed for closed-app FleetFix alerts.'
            : 'Ready to subscribe this browser for closed-app FleetFix alerts.'
    };
  }

  public async subscribeToWebPush(configUpdates?: Partial<WebPushConfig>): Promise<WebPushStatus> {
    if (configUpdates) {
      const current = this.getWebPushConfig();
      localStorage.setItem(this.getUserScopedStorageKey(this.webPushConfigKey), JSON.stringify({ ...current, ...configUpdates }));
    }

    const status = this.getWebPushStatus();
    if (!status.supported || !status.configured) return status;

    const permission = await this.requestDesktopPermission();
    if (permission !== 'granted') {
      return {
        ...this.getWebPushStatus(),
        permission,
        message: 'Browser notification permission is required before Web Push can be enabled.'
      };
    }

    const registration = this.serviceWorkerRegistration || await navigator.serviceWorker.register('/fleetfix-notifications-sw.js');
    this.serviceWorkerRegistration = registration;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.getWebPushConfig().vapidPublicKey)
      });
    }

    localStorage.setItem(this.getUserScopedStorageKey(this.webPushSubscriptionKey), JSON.stringify(subscription.toJSON()));

    const pushConfig = this.getWebPushConfig();
    let endpointSyncWarning = '';
    if (pushConfig.subscriptionEndpoint) {
      try {
        const response = await fetch(pushConfig.subscriptionEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subscription: subscription.toJSON(),
            source: 'fleetfix-web',
            userId: this.currentUserId,
            createdAt: new Date().toISOString()
          })
        });
        if (!response.ok) endpointSyncWarning = ` Browser subscribed, but endpoint sync returned ${response.status}.`;
      } catch (error) {
        endpointSyncWarning = ' Browser subscribed, but FleetFix could not reach the push endpoint here.';
      }
    }

    return {
      ...this.getWebPushStatus(),
      subscribed: true,
      permission: 'granted',
      message: pushConfig.subscriptionEndpoint
        ? `Closed-app Web Push is subscribed${endpointSyncWarning || ' and synced to the push endpoint.'}`
        : 'Closed-app Web Push is subscribed locally. Add a server endpoint to send alerts when FleetFix is closed.'
    };
  }

  public async unsubscribeFromWebPush(): Promise<WebPushStatus> {
    if ('serviceWorker' in navigator) {
      const registration = this.serviceWorkerRegistration || await navigator.serviceWorker.getRegistration('/fleetfix-notifications-sw.js');
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    }

    localStorage.removeItem(this.getUserScopedStorageKey(this.webPushSubscriptionKey));
    return {
      ...this.getWebPushStatus(),
      subscribed: false,
      message: 'Closed-app push has been turned off for this browser.'
    };
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let index = 0; index < rawData.length; index += 1) {
      outputArray[index] = rawData.charCodeAt(index);
    }

    return outputArray;
  }

  private isQuietHours(): boolean {
    if (!this.preferences.quietHours.enabled) return false;
    
    const now = new Date();
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    const startTime = parseInt(this.preferences.quietHours.start.replace(':', ''));
    const endTime = parseInt(this.preferences.quietHours.end.replace(':', ''));
    
    if (startTime > endTime) {
      
      return currentTime >= startTime || currentTime <= endTime;
    } else {
      return currentTime >= startTime && currentTime <= endTime;
    }
  }

  private startMonitoring(): void {
    
    setInterval(() => {
      this.checkForMaintenanceDue();
      this.checkForLowFuel();
      this.checkForLowParts();
    }, 30000);
  }

  private checkForMaintenanceDue(): void {
    const trucks = this.getCurrentAppState().trucks || [];
    trucks.forEach((truck: any) => {
      if (truck.mileage && truck.lastMaintenance) {
        const milesSinceLastMaintenance = truck.mileage - truck.lastMaintenance;
        const maintenanceInterval = 5000; 
        
        if (milesSinceLastMaintenance > maintenanceInterval - 500) {
          const existingNotification = this.notifications.find(n => 
            n.relatedEntity?.id === truck.id && n.type === 'maintenance' && !n.read
          );
          
          if (!existingNotification) {
            this.addNotification({
              type: 'maintenance',
              title: 'Maintenance Due Soon',
              message: `${truck.make} ${truck.model} (${truck.id}) needs maintenance in ${formatDistance(maintenanceInterval - milesSinceLastMaintenance, { longUnit: true })}`,
              priority: 'high',
              actionRequired: true,
              relatedEntity: {
                type: 'truck',
                id: truck.id,
                name: `${truck.make} ${truck.model}`
              }
            });
          }
        }
      }
    });
  }

  private checkForLowFuel(): void {
    const trucks = this.getCurrentAppState().trucks || [];
    const fuelThreshold = JSON.parse(localStorage.getItem('fleetSettings') || '{}').lowFuelThreshold || 20;
    
    trucks.forEach((truck: any) => {
      if (truck.fuelLevel && truck.fuelLevel <= fuelThreshold) {
        const existingNotification = this.notifications.find(n => 
          n.relatedEntity?.id === truck.id && n.type === 'fuel' && !n.read
        );
        
        if (!existingNotification) {
          this.addNotification({
            type: 'fuel',
            title: 'Low Fuel Alert',
            message: `${truck.make} ${truck.model} (${truck.id}) fuel level at ${truck.fuelLevel}%`,
            priority: 'urgent',
            actionRequired: true,
            relatedEntity: {
              type: 'truck',
              id: truck.id,
              name: `${truck.make} ${truck.model}`
            }
          });
        }
      }
    });
  }

  private checkForLowParts(): void {
    const parts = this.getCurrentAppState().parts || [];
    
    parts.forEach((part: any) => {
      const currentStock = part.inventoryLevel || 0;
      const minStock = part.minStockLevel || 5;
      
      if (currentStock <= minStock) {
        const existingNotification = this.notifications.find(n => 
          n.relatedEntity?.id === part.id && n.type === 'part' && !n.read
        );
        
        if (!existingNotification) {
          const alertType = currentStock === 0 ? 'out_of_stock' : 'low_stock';
          const priority = currentStock === 0 ? 'urgent' : 'high';
          
          this.addNotification({
            type: 'part',
            title: alertType === 'out_of_stock' ? 'Out of Stock Alert' : 'Low Stock Alert',
            message: alertType === 'out_of_stock' 
              ? `${part.name} is out of stock. Immediate reorder required.`
              : `${part.name} is running low - ${currentStock} remaining (minimum: ${minStock})`,
            priority: priority,
            actionRequired: true,
            relatedEntity: {
              type: 'part',
              id: part.id,
              name: part.name
            }
          });
        }
      }
    });
  }

  public addNotification(data: Omit<Notification, 'id' | 'timestamp' | 'read'>): void {
    const notification: Notification = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      read: false,
      ...data
    };

    this.notifications.unshift(notification);
    this.saveNotifications();
    this.notifyListeners();
    void this.sendNotification(notification);
  }

  private async sendNotification(notification: Notification): Promise<void> {
    if (this.isQuietHours()) {
      console.log('Notification suppressed due to quiet hours:', notification.title);
      return;
    }

    if (!this.canSendDesktopNotifications()) return;

    const notificationOptions: NotificationOptions = {
      body: notification.message,
      icon: '/logo192.png',
      badge: '/favicon.ico',
      tag: notification.relatedEntity ? `${notification.type}-${notification.relatedEntity.id}` : notification.id,
      requireInteraction: notification.priority === 'urgent',
      data: {
        url: this.getNotificationUrl(notification)
      }
    };

    if ('serviceWorker' in navigator) {
      try {
        const registration = this.serviceWorkerRegistration || await navigator.serviceWorker.getRegistration('/fleetfix-notifications-sw.js');
        if (registration) {
          await registration.showNotification(`FleetFix: ${notification.title}`, notificationOptions);
          return;
        }
      } catch (error) {
        console.error('Error showing service worker notification:', error);
      }
    }

    try {
      new Notification(`FleetFix: ${notification.title}`, notificationOptions);
    } catch (error) {
      console.error('Error showing browser notification:', error);
    }
  }

  private getNotificationUrl(notification: Notification): string {
    switch (notification.relatedEntity?.type || notification.type) {
      case 'truck':
        return notification.relatedEntity?.id ? `/trucks/${notification.relatedEntity.id}` : '/trucks';
      case 'part':
        return '/parts';
      case 'maintenance':
        return '/maintenance';
      default:
        return '/dashboard';
    }
  }

  private getStoredWebPushSubscription(): PushSubscriptionJSON | null {
    const rawSubscription = localStorage.getItem(this.getUserScopedStorageKey(this.webPushSubscriptionKey));
    if (!rawSubscription) return null;
    try {
      return JSON.parse(rawSubscription) as PushSubscriptionJSON;
    } catch {
      return null;
    }
  }

  private getWebPushTestEndpoint(): string {
    const configured = process.env.REACT_APP_PUSH_TEST_ENDPOINT?.trim();
    if (configured) return configured;
    const subscriptionEndpoint = this.getWebPushConfig().subscriptionEndpoint;
    return subscriptionEndpoint ? subscriptionEndpoint.replace(/\/subscribe\/?$/, '/test') : '';
  }

  public async sendTestNotification(): Promise<void> {
    const permission = await this.requestDesktopPermission();
    if (permission !== 'granted') return;

    const storedSubscription = this.getStoredWebPushSubscription();
    const testEndpoint = this.getWebPushTestEndpoint();
    if (storedSubscription && testEndpoint) {
      try {
        const response = await fetch(testEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subscription: storedSubscription,
            payload: {
              title: 'FleetFix Push Test',
              body: 'Server Web Push is working for this computer.',
              url: '/notifications',
              priority: 'medium',
              tag: 'fleetfix-push-test'
            }
          })
        });
        if (response.ok) return;
      } catch (error) {
        console.warn('Server Web Push test failed; falling back to local notification.', error);
      }
    }

    await this.sendNotification({
      id: 'fleetfix-test-notification',
      type: 'info',
      title: 'Desktop Notifications Enabled',
      message: 'FleetFix can now send alerts to this computer.',
      timestamp: new Date(),
      read: false,
      priority: 'medium',
      actionRequired: false
    });
  }

  public getNotifications(): Notification[] {
    return [...this.notifications].sort((a, b) => this.normalizeTimestamp(b.timestamp).getTime() - this.normalizeTimestamp(a.timestamp).getTime());
  }

  public getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  public markAsRead(notificationId: string): void {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.saveNotifications();
      this.notifyListeners();
    }
  }

  public markAllAsRead(): void {
    this.notifications.forEach(n => n.read = true);
    this.saveNotifications();
    this.notifyListeners();
  }

  public deleteNotification(notificationId: string): void {
    this.notifications = this.notifications.filter(n => n.id !== notificationId);
    this.saveNotifications();
    this.notifyListeners();
  }

  public clearAllNotifications(): void {
    this.notifications = [];
    this.saveNotifications();
    this.notifyListeners();
  }

  public updatePreferences(preferences: Partial<NotificationPreferences>): void {
    this.preferences = { ...this.preferences, ...preferences };
    this.savePreferences();
  }

  public getPreferences(): NotificationPreferences {
    return { ...this.preferences };
  }

  public subscribe(listener: (notifications: Notification[]) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getNotifications()));
  }

  public formatTimeAgo(timestamp: Date): string {
    const now = new Date();
    const safeTimestamp = this.normalizeTimestamp(timestamp);
    const diffMs = now.getTime() - safeTimestamp.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return safeTimestamp.toLocaleDateString();
  }

  public createTestNotifications(): void {
    return;
  }

  public forceCheckAllAlerts(): void {
    this.checkForMaintenanceDue();
    this.checkForLowFuel();
    this.checkForLowParts();
  }

  public checkPartsStock(parts: any[]): void {
    parts.forEach((part: any) => {
      const currentStock = part.inventoryLevel || 0;
      const minStock = part.minStockLevel || 5;
      
      if (currentStock <= minStock) {
        const existingNotification = this.notifications.find(n => 
          n.relatedEntity?.id === part.id && n.type === 'part' && !n.read
        );
        
        if (!existingNotification) {
          const alertType = currentStock === 0 ? 'out_of_stock' : 'low_stock';
          const priority = currentStock === 0 ? 'urgent' : 'high';
          
          this.addNotification({
            type: 'part',
            title: alertType === 'out_of_stock' ? 'Out of Stock Alert' : 'Low Stock Alert',
            message: alertType === 'out_of_stock' 
              ? `${part.name} is out of stock. Immediate reorder required.`
              : `${part.name} is running low - ${currentStock} remaining (minimum: ${minStock})`,
            priority: priority,
            actionRequired: true,
            relatedEntity: {
              type: 'part',
              id: part.id,
              name: part.name
            }
          });
        }
      } else {
        // Clear any existing low stock notifications for this part when stock is sufficient
        const existingNotifications = this.notifications.filter(n => 
          n.relatedEntity?.id === part.id && n.type === 'part' && !n.read
        );
        
        existingNotifications.forEach(notification => {
          this.markAsRead(notification.id);
        });
      }
    });
  }


  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }
}


export const notificationService = NotificationService.getInstance();
export type { Notification, NotificationPreferences, WebPushConfig, WebPushStatus };
